import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FileSpreadsheetIcon, MailIcon, CalendarIcon, ReceiptIcon, LayoutDashboardIcon } from 'lucide-react';

// Schematisch beeld: losse tools links komen samen in één webapplicatie
// rechts. Bewust zonder cijfers of verzonnen data.
const sources = [{
  label: 'Excel',
  icon: FileSpreadsheetIcon
}, {
  label: 'Mail',
  icon: MailIcon
}, {
  label: 'Agenda',
  icon: CalendarIcon
}, {
  label: 'Boekhouding',
  icon: ReceiptIcon
}];
const EASE = [0.23, 1, 0.32, 1];
export const WebappHeroVisual: React.FC = () => {
  const reduceMotion = useReducedMotion();
  return <div className="relative bg-white rounded-2xl p-5 sm:p-6 md:p-8 shadow-2xl" role="img" aria-label="Losse tools zoals Excel, mail, agenda en boekhouding komen samen in één webapplicatie" data-id="element-951" data-mp="2jkycds23lnmj">
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-3" data-id="element-952" data-mp="1qw9gq8jtaai6">
        {/* Losse tools */}
        <ul className="space-y-3" data-id="element-953" data-mp="1vuksioto2v31">
          {sources.map((source, index) => {
          const Icon = source.icon;
          return <motion.li key={source.label} initial={reduceMotion ? false : {
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.25,
            delay: 0.15 + index * 0.05,
            ease: EASE
          }} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-[#F7F8FA] px-2.5 py-2 sm:px-3" data-id="element-954" data-mp="2i9j0uytrwr99" data-mpi={index}>
                <Icon className="h-4 w-4 flex-shrink-0 text-[#6B7280]" aria-hidden="true" data-id="element-955" data-mp="1ui550iweydjj" />
                <span className="text-xs sm:text-sm font-medium text-[#111827] whitespace-nowrap" data-id="element-956" data-mp="1pisjq2l6xej2">
                  {source.label}
                </span>
              </motion.li>;
        })}
        </ul>

        {/* Verbindingslijnen */}
        <svg viewBox="0 0 100 200" preserveAspectRatio="none" className="h-48 w-full" aria-hidden="true" data-id="element-957" data-mp="3jvqraefi9x2p">
          {[25, 75, 125, 175].map((y, index) => <motion.path key={y} d={`M 0 ${y} C 50 ${y}, 50 100, 100 100`} fill="none" stroke="#004AC7" strokeWidth="1.5" strokeOpacity="0.45" vectorEffect="non-scaling-stroke" initial={reduceMotion ? false : {
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          delay: 0.35 + index * 0.05,
          ease: EASE
        }} data-id="element-958" data-mp="3evm6ic7keg6o" data-mpi={index} />)}
        </svg>

        {/* De webapp */}
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25,
        delay: 0.6,
        ease: EASE
      }} className="w-28 sm:w-40 rounded-xl border-2 border-[#004AC7] bg-white overflow-hidden" data-id="element-959" data-mp="1vyrbv8wbl01d">
          <div className="flex items-center gap-1.5 bg-[#004AC7] px-3 py-2" data-id="element-960" data-mp="1qyd2nnizfk1r">
            <LayoutDashboardIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-961" data-mp="bi4gzo5ib9cp" />
            <span className="text-xs font-semibold text-white whitespace-nowrap" data-id="element-962" data-mp="fl16lytgs5q5">
              Jouw webapp
            </span>
          </div>
          <div className="space-y-2 p-3" data-id="element-963" data-mp="7zf6b2x82mbw">
            <div className="h-2 w-3/4 rounded-full bg-[#DCE6FA]" data-id="element-964" data-mp="32f7nd2pclut" />
            <div className="h-2 w-1/2 rounded-full bg-gray-200" data-id="element-965" data-mp="349k02zwr5na" />
            <div className="grid grid-cols-2 gap-1.5 pt-1" data-id="element-966" data-mp="hy4pr04roeu9">
              <div className="h-8 rounded-md bg-[#F7F8FA] border border-gray-100" data-id="element-967" data-mp="d0me31zd7lik" />
              <div className="h-8 rounded-md bg-[#F7F8FA] border border-gray-100" data-id="element-968" data-mp="3lg629ml6idqr" />
            </div>
            <div className="h-2 w-2/3 rounded-full bg-gray-200" data-id="element-969" data-mp="3gh38wzek9vym" />
          </div>
        </motion.div>
      </div>

      <p className="mt-6 pt-5 border-t border-gray-200 text-sm text-[#6B7280] leading-relaxed" data-id="element-970" data-mp="3v5po9146unpu">
        Eén plek waar je gegevens samenkomen, ingericht op hoe jouw bedrijf
        werkt.
      </p>
    </div>;
};